import type {
  ActivityCockpitSnapshot,
  CockpitConfidence,
  CockpitSection,
  CockpitSkillInvocation,
} from '@internal/spassu-code-contract';
import { createCockpitDeliveryView } from './createCockpitDeliveryView';
import { createCockpitGuaranteeView } from './createCockpitGuaranteeView';
import { createCockpitHeaderView } from './createCockpitHeaderView';
import { createCockpitMetricViews } from './createCockpitMetricViews';
import { createCockpitPhaseSteps } from './createCockpitPhaseSteps';
import type {
  CockpitRailSegment,
  CockpitRailState,
  CockpitSessionObservabilityView,
  CockpitSessionOutcomeView,
  CockpitSkillView,
  CockpitViewModel,
} from './cockpitViewModel';
import {
  sessionClosureOutcomeLabel,
  sessionRuntimeLabel,
} from './semanticLabels';

export interface CockpitRailSemanticInput {
  category: string;
  skillLabel: string | null;
  confidence: CockpitConfidence | null;
}

export interface CockpitRailPresentation {
  label: string;
  state: CockpitRailState;
}

export interface CockpitViaSemantics {
  viaLabel: string;
  presentRail(input: CockpitRailSemanticInput): CockpitRailPresentation;
  presentSessionObservability?(
    snapshot: ActivityCockpitSnapshot,
  ): CockpitSessionObservabilityView | null;
}

export function adaptCockpitSnapshot(
  snapshot: ActivityCockpitSnapshot,
  semantics: CockpitViaSemantics,
): CockpitViewModel {
  const summary = snapshot.sections.summary.value;
  if (!summary) throw new Error(`${semantics.viaLabel} cockpit indisponível.`);
  const skills = mapSkills(snapshot.sections.skills);
  const header = createCockpitHeaderView(snapshot, semantics.viaLabel);
  return {
    activity: summary.activity,
    header,
    revision: snapshot.revision,
    generatedAt: snapshot.generatedAt,
    sessionObservability:
      semantics.presentSessionObservability?.(snapshot) ?? null,
    runtimeLabel: header.runtimeLabel,
    phases: createCockpitPhaseSteps(snapshot),
    delivery: createCockpitDeliveryView(snapshot),
    guarantee: createCockpitGuaranteeView(snapshot),
    rail: mapRail(snapshot, skills, semantics),
    skills,
    artifacts: (snapshot.sections.artifacts.value ?? []).map(artifact => ({
      id: artifact.id,
      kind: 'artifact',
      label: artifact.name,
      artifactType: artifact.artifactType ?? null,
      phase: artifact.phase ?? null,
      path: artifact.path ?? null,
      skillName: artifact.skillName ?? null,
      confidence: artifact.confidence,
      lifecycle:
        artifact.lifecycle === 'unknown' ? 'interrupted' : artifact.lifecycle,
      coverage: snapshot.sections.artifacts.coverage,
      role: artifact.role,
      access: artifact.access,
      version: artifact.version,
      revisionKey: artifact.revisionKey,
      provenance: artifact.provenance,
      createdAt: artifact.createdAt,
      invocationId: artifact.invocationId,
      evidenceIds: artifact.evidenceIds,
      limitations: artifact.limitations,
    })),
    metrics: createCockpitMetricViews(snapshot),
    sessions: mapSessions(snapshot),
    relations: snapshot.sections.relations.value ?? [],
    sectionState: {
      relations: sectionState(snapshot.sections.relations),
      artifacts: sectionState(snapshot.sections.artifacts),
    },
  };
}

function mapSkills(
  section: ActivityCockpitSnapshot['sections']['skills'],
): CockpitSkillView[] {
  return (section.value ?? []).map(skill => ({
    id: skill.id,
    kind: 'skill',
    label: skill.skillName,
    confidence: section.confidence,
    lifecycle: skill.lifecycle,
    coverage: section.coverage,
    version: skill.version,
    sourceLabel: skillSourceLabel(skill),
    attempt: skill.attempt,
    durationMs: skill.durationMs,
    evidenceIds: skill.evidenceIds,
    artifactIds: skill.artifactIds,
    limitations: skill.limitations,
  }));
}

function mapRail(
  snapshot: ActivityCockpitSnapshot,
  skills: CockpitSkillView[],
  semantics: CockpitViaSemantics,
): CockpitRailSegment[] {
  const invocations = snapshot.sections.skills.value ?? [];
  return (snapshot.sections.time.value?.intervals ?? []).map(interval => {
    const invocation = invocations.find(item =>
      item.evidenceIds.some(id => interval.evidenceIds.includes(id)),
    );
    const skill = skills.find(item => item.id === invocation?.id);
    const presentation = semantics.presentRail({
      category: interval.category,
      skillLabel: skill?.label ?? null,
      confidence: skill?.confidence ?? null,
    });
    return {
      id: interval.id,
      nodeId: skill?.id ?? null,
      ...presentation,
      durationMs: interval.durationMs,
      startedAt: interval.startedAt,
      endedAt: interval.endedAt,
    };
  });
}

/**
 * Optional on snapshots stored before 2026-09-10 (ADR-005) — an absent
 * section reads as no bindings to list, not a crash.
 */
function mapSessions(
  snapshot: ActivityCockpitSnapshot,
): CockpitSessionOutcomeView[] {
  const rows = snapshot.sections.sessions?.value ?? [];
  return rows.map(row => ({
    bindingId: row.bindingId,
    runtimeLabel: sessionRuntimeLabel(row.runtimeKind),
    issuedAt: row.issuedAt,
    closedAt: row.closedAt,
    usages: row.usages,
    turnsUser: row.turns.user,
    turnsAgent: row.turns.agent,
    outcomeLabel: sessionClosureOutcomeLabel(row.outcome, row.closedAt),
    active: row.outcome === null && row.closedAt === null,
  }));
}

function sectionState<T>(section: CockpitSection<T>) {
  return {
    status: section.status,
    coverage: section.coverage,
    limitations: section.limitations,
  };
}

// An unknown source stays empty so the presentation can word it once, next to
// its own label; a placeholder here produced "Origem: Origem não informada".
function skillSourceLabel(skill: CockpitSkillInvocation): string {
  const runtime = [skill.provider, skill.model].filter(Boolean).join(' · ');
  return runtime || skill.agentId || '';
}
